import type { AxiosError } from 'axios';
import type { SWRInfiniteConfiguration, SWRInfiniteKeyLoader } from 'swr/infinite';
import useSWRInfinite from 'swr/infinite';

//hooks
import useUser from '@/hooks/swr/useUser';

// Utils
import { get } from '@/utils/client';

//types
import type { DataReceiptsType } from '@/utils/hooks/swr/pricing/useReceipts/types';

// API Endpoints
import { BILLING_RECEIPTS } from '@/lib/routes/apiEndpoints';

const getKey: SWRInfiniteKeyLoader<DataReceiptsType, string | null> = (
    pageIndex,
    previousPageData
) => {
    // Reached the end
    if (previousPageData && !previousPageData.data) return null;

    // First page, we don't have `previousPageData`
    if (pageIndex === 0) return BILLING_RECEIPTS;

    // Add the cursor to the API endpoint
    return `${BILLING_RECEIPTS}?cursor=${previousPageData.cursor}`;
};

const useReceipts = (options?: SWRInfiniteConfiguration<DataReceiptsType, AxiosError<Error>>) => {
    const { user } = useUser();

    const {
        data: dataReceipts,
        size,
        setSize,
        mutate: mutateReceipts,
        error: receiptsError
    } = useSWRInfinite<DataReceiptsType, AxiosError<Error>>(
        (pageIndex, previousPageData) => {
            const url = getKey(pageIndex, previousPageData);
            return [url, user?.id];
        },
        ([url]) => get(url).then((res) => res.data),
        {
            revalidateIfStale: false,
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    const receipts = dataReceipts?.flatMap(({ data }) => data) || [];

    const isLoadingInitialData = !dataReceipts && !receiptsError;
    const isLoadingMore = size > 0 && dataReceipts && typeof dataReceipts[size - 1] === 'undefined';

    return {
        receipts,
        loadMoreReceipts: () => setSize(size + 1), // Function to load more receipts (load more button)
        mutateReceipts,
        receiptsError,
        isLoadingInitialData, // Check if it's the initial loading (not the load more)
        canLoadMore:
            dataReceipts && typeof dataReceipts[dataReceipts.length - 1].cursor === 'string', // Check if there is a cursor to active the load more button
        isLoadingMore // Check if the load more is loading
    };
};

export default useReceipts;
